import React, { useMemo, useCallback, useState } from "react";
import { CommentContentText } from "./styles";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import ListItemIcon from "@mui/material/ListItemIcon";
import SvgIcon from "@mui/material/SvgIcon";
import { SwapHorizOutlined } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  TransactionNotificationComment,
  LeaseEventNotificationComment,
  PreTransactionNotificationComment,
  LeaseEvent,
  Transaction,
  TransactionDeleteRequest,
  PreTransactionDeleteRequest,
} from "../../../../shared/utils/constants/index";
import * as userActions from "redux/actions/userActions";
import "./styles.css";
import { CircularProgress } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

function EllipseIcon(props) {
  const memoizedIcon = useMemo(
    () => (
      <SvgIcon viewBox="0 0 12 12" {...props}>
        <ellipse cx="6" cy="6" rx="4" r="6" fill="#40798D" />
      </SvgIcon>
    ),
    [props]
  );

  return memoizedIcon;
}

const NotificationList = (props) => {
  const { data, onClose, loading } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const [selectedIndex, setIndex] = useState(-1);
  const userId = useSelector((state) => state.currentUser.details.id);
  const selectedClientId = useSelector(
    (state) => state.clients.selectedClientId
  );
  const clientUsers = useSelector((state) => state.clients.clientUsers);

  const formatDisplayName = (email) => {
  if (!email) return "";
  return email
    .split("@")[0]
    .split(".")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
  };

  const userOptions = React.useMemo(() => {
      if (!clientUsers || !Array.isArray(clientUsers) || clientUsers.length === 0) {
        return [];
      }
      const users = clientUsers.map((x) => ({
        id: x.userEmailAddress,
        label: formatDisplayName(x.userEmailAddress),
        email: x.userEmailAddress
      }));
      return users;
    }, [clientUsers]);

  const processCommentForMentions = (commentHTML) => {
    if (!commentHTML || !userOptions || userOptions.length === 0) return commentHTML;


    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = commentHTML;

    // Find all text nodes and check if they match user names
    const walker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      textNodes.push(node);
    }


    textNodes.forEach((textNode, nodeIndex) => {
      const text = textNode.textContent;
      if (!text || text.trim().length === 0) return;


      // Check if this text matches any user name (with or without @)
      const cleanText = text.trim().replace(/^@/, '');
      const user = userOptions.find(u => 
        cleanText.toLowerCase() === u.label.toLowerCase()
      );

      if (user) {
        
        // Replace the text node with a mention span
        const mentionSpan = document.createElement('span');
        mentionSpan.className = 'mention';
        mentionSpan.setAttribute('data-user-id', user.id);
        mentionSpan.setAttribute('data-user-email', user.email);
        mentionSpan.setAttribute('data-user-label', user.label);
        mentionSpan.style.color = '#09779E';
        mentionSpan.style.textDecoration = 'none';
        mentionSpan.style.cursor = 'pointer';
        // Keep pointer events enabled for tooltip hover
        mentionSpan.textContent = user.label; // Use the formatted display name, not the original text

        textNode.parentNode.replaceChild(mentionSpan, textNode);
      }
    });

    const result = tempDiv.innerHTML;
    return result;
  };


  const handleListItemClick = useCallback(
    (index, notification) => {
      setIndex(index);
      dispatch(userActions.resetSelectedNotificationComment());
      dispatch(userActions.setSelectedNotificationComment(notification));

      if (notification.id && !notification.isRead) {
        Promise.all([
          dispatch(
            userActions.MarkNotificationsAsRead(userId, notification.id)
          ),
        ])
          .catch((e) => {
            console.log(`Error: ${e}`);
          })
          .finally(() => {
            dispatch(
              userActions.getUnreadNotificationCount(userId, selectedClientId)
            );
          });
      }

      if (notification.type === TransactionNotificationComment) {
        history.push(`/transaction/comments/${notification.typeEntityId}`);
      } else if (notification.type === PreTransactionNotificationComment) {
        history.push(`/pre-transaction/comments/${notification.typeEntityId}`);
      } else if (
        notification.type === LeaseEventNotificationComment ||
        notification.type === LeaseEvent
      ) {
        history.push(`/lease-event`);
      } else if (notification.type === Transaction) {
        history.push(`/transaction`);
      }
      else if(notification.type === TransactionDeleteRequest || notification.type === PreTransactionDeleteRequest) {
        history.push(`/admin/account-admin`);
      }

      onClose();
    },
    [dispatch, history, onClose, selectedClientId, userId]
  );

  return (
    <Box>
      <List component="nav" disablePadding className="notification-list">
        {(Array.isArray(data) ? data : []).map((item, index) => (         
          <React.Fragment key={item?.id ?? `${item?.typeEntityId ?? "notification"}-${index}`}>             
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedIndex === index}
                onClick={(event) => handleListItemClick(index, item)}
              >
                <ListItemIcon>
                  {!item.isRead && (
                    <EllipseIcon color="primary" fontSize="small" />
                  )}
                </ListItemIcon>

                <ListItemText
                  primary={
                    <React.Fragment>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          top: "-5px",
                          position: "relative",
                        }}
                      >
                        <Typography
                          variant="body1"
                          className={
                            item.isRead === true ? "category-read" : "category"
                          }
                        >
                          {item.category}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            textTransform: "none",
                            fontFamily: "Source Sans Pro, sans-serif",
                            lineHeight: "1.9",
                          }}
                        >
                          {item.timeStamp}
                        </Typography>
                      </div>
                    </React.Fragment>
                  }
                  secondary={
                    <React.Fragment>
                      <div className="comment-message-1">
                        <Typography
                          component="span"
                          variant="body2"
                          className={
                            item.isRead === true ? "username-read" : "username"
                          }
                        >
                          {item.type === LeaseEvent || item.type === Transaction
                            ? `${item.typeEntityName}${
                                item.notificationCount > 1
                                  ? ` +${item.notificationCount - 1}`
                                  : ""
                              }`
                            : (item.type === TransactionDeleteRequest || item.type === PreTransactionDeleteRequest)
                              ? `${item.fromUserLastName}, ${item.fromUserFirstName}`
                              : `${item.fromUserLastName}, ${item.fromUserFirstName}:`}
                        </Typography>
                        {item.type === LeaseEvent || item.type === Transaction || item.type === TransactionDeleteRequest || item.type === PreTransactionDeleteRequest
                            ?  <Typography
                                component="span"
                                variant="body2"
                                className="message"
                               >{item.notificationMessage}</Typography>:
                               <CommentContentText
                                  dangerouslySetInnerHTML={{ __html: processCommentForMentions(item.notificationMessage) }}
                                />
                        }
                        
                      </div>
                      <div className="comment-message-2">
                        <Typography
                          component="span"
                          className="entity-name"
                          variant="body2"
                        >
                          {item.type === LeaseEvent ||
                          item.type === Transaction ? (
                            <>
                              <CalendarTodayIcon fontSize="small" />
                              <span className="due-date">
                                {item.type === LeaseEvent &&
                                  "Lease Notice Date"}
                                {item.type === Transaction && "Due Date"}
                                {`: ${new Date(
                                  item.typeEntityDueDate
                                ).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })}`}
                              </span>
                            </>
                          ) : (
                            <>
                              <SwapHorizOutlined fontSize="small" />
                              {(item.type === TransactionNotificationComment || item.type === TransactionDeleteRequest) &&
                                "Transaction Name"}
                              {item.type === LeaseEventNotificationComment &&
                                "Lease Name"}
                              {(item.type ===
                                PreTransactionNotificationComment || item.type === PreTransactionDeleteRequest) &&
                                "Pre-Transaction Name"}
                              {`: ${item.typeEntityName}`}
                            </>
                          )}
                        </Typography>
                      </div>
                    </React.Fragment>
                  }
                />
              </ListItemButton>
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
        {loading && (
          <React.Fragment>
            <ListItem>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 2,
                }}
              >
                <CircularProgress size={32} sx={{ color: "#001F2B" }} />
              </Box>
            </ListItem>
            <Divider />
          </React.Fragment>
        )}
      </List>
    </Box>
  );
};

export default NotificationList;
